import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RefreshCw } from "lucide-react";
import type { ChannelUsageSummary, UsageAttemptStatus } from "@/api/client";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL: Record<UsageAttemptStatus, string> = {
  success: "正常",
  failed: "采集失败",
  unsupported: "暂不支持",
  no_data: "暂无逐渠道数据",
};

function formatUsage(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 10, useGrouping: true }).format(value);
}

export function OperatorUsageCell({
  summary,
  loading,
  failed,
  refreshing,
  refreshDisabled,
  onRefresh,
}: {
  summary?: ChannelUsageSummary;
  loading: boolean;
  failed: boolean;
  refreshing: boolean;
  refreshDisabled: boolean;
  onRefresh: () => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [position, setPosition] = useState<{ left: number; top?: number; bottom?: number; maxHeight: number } | null>(null);
  const tooltipId = summary ? `usage-detail-${summary.key_id}` : undefined;

  const open = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const viewportGap = 8;
    const anchorGap = 4;
    const tooltipMaxHeight = 420;
    const width = Math.min(360, window.innerWidth - viewportGap * 2);
    const availableBelow = window.innerHeight - rect.bottom - anchorGap - viewportGap;
    const availableAbove = rect.top - anchorGap - viewportGap;
    const openAbove = availableBelow < tooltipMaxHeight && availableAbove > availableBelow;
    const availableHeight = openAbove ? availableAbove : availableBelow;
    setPosition({
      left: Math.max(viewportGap, Math.min(rect.left, window.innerWidth - width - viewportGap)),
      maxHeight: Math.max(0, Math.min(tooltipMaxHeight, availableHeight)),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + anchorGap }
        : { top: rect.bottom + anchorGap }),
    });
  };
  const closeSoon = () => {
    closeTimer.current = setTimeout(() => setPosition(null), 120);
  };

  let valueLabel = "—";
  let secondary = "暂无数据";
  if (loading) secondary = "加载中";
  else if (failed) secondary = "获取失败";
  else if (summary?.source_site_count === 0) secondary = "未分发";
  else if (summary?.total != null) {
    valueLabel = `$${formatUsage(summary.total)}`;
    const has30DayValue = summary.sources.some((source) => source.total != null && source.metric_key === "20218912178.channel.usage_usd_30d");
    secondary = [summary.coverage === "partial" ? "部分数据" : "", has30DayValue ? "含近30天" : ""].filter(Boolean).join(" · ");
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <button
        ref={anchorRef}
        type="button"
        className="rounded px-1 py-0.5 text-left font-mono text-xs outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
        onMouseEnter={open}
        onMouseLeave={closeSoon}
        onFocus={open}
        onBlur={closeSoon}
        aria-describedby={tooltipId}
        aria-expanded={position != null}
      >
        <span className={summary?.total != null ? "font-semibold text-foreground" : "text-muted-foreground"}>{valueLabel}</span>
        {secondary && <span className={`ml-1.5 font-sans text-[11px] ${summary?.coverage === "partial" ? "text-amber-600" : "text-muted-foreground"}`}>{secondary}</span>}
      </button>
      <button
        type="button"
        className="rounded p-1 text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40"
        onClick={onRefresh}
        disabled={refreshDisabled}
        title="只刷新该渠道用量"
        aria-label="只刷新该渠道用量"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
      </button>
      {position && summary && createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          className="fixed z-[100] flex w-[22rem] max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-lg border bg-card p-2.5 text-card-foreground shadow-2xl"
          style={{ left: position.left, top: position.top, bottom: position.bottom, maxHeight: position.maxHeight }}
          onMouseEnter={() => closeTimer.current && clearTimeout(closeTimer.current)}
          onMouseLeave={closeSoon}
        >
          <div className="mb-2 flex shrink-0 items-center justify-between gap-3 border-b pb-2">
            <div>
              <div className="text-sm font-semibold">渠道用量明细</div>
              <div className="text-[11px] text-muted-foreground">{summary.valued_source_count}/{summary.source_site_count} 个分发目标有值 · 最新采集值</div>
            </div>
            <Badge variant={summary.coverage === "complete" ? "success" : summary.coverage === "partial" ? "warning" : "muted"}>
              {summary.coverage === "complete" ? "完整" : summary.coverage === "partial" ? "部分数据" : "暂无数据"}
            </Badge>
          </div>
          {summary.sources.length === 0 ? (
            <div className="py-2 text-xs text-muted-foreground">该渠道尚未成功分发到下游站点。</div>
          ) : (
            <div className="min-h-0 space-y-2 overflow-y-auto pr-1">
              {summary.sources.map((source) => (
                <div key={`${source.source_site_type}:${source.source_site_id}`} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 rounded-md border bg-secondary px-2.5 py-2 text-xs">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate font-medium">{source.source_site_name || `${source.source_site_type} #${source.source_site_id}`}</span>
                      <Badge variant="muted" className="shrink-0 border px-1.5 py-0 text-[10px] font-normal">{source.source_site_type === "relay" ? "中转站" : "推送站"}</Badge>
                    </div>
                    <div className="mt-1 font-mono text-[10px] text-muted-foreground">
                      {source.target_code ? `发布编号 ${source.target_code} · ` : ""}
                      {source.metric_key === "20218912178.channel.usage_usd_30d" ? "近 30 天 · " : ""}
                      {source.last_success_at ? `采集于 ${new Date(source.last_success_at).toLocaleString("zh-CN", { hour12: false })}` : "从未成功采集"}
                      <span className={source.status === "success" ? "ml-1.5 text-green-600" : source.status === "failed" ? "ml-1.5 text-destructive" : "ml-1.5 text-amber-600"}>· {STATUS_LABEL[source.status]}</span>
                    </div>
                  </div>
                  <div className="self-center whitespace-nowrap font-mono font-semibold">
                    {source.total == null ? "—" : `$${formatUsage(source.total)}`}
                    {source.coverage === "partial" && <span className="ml-1 font-sans text-[10px] font-normal text-muted-foreground">部分</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
